import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * PortalCallbackPage
 * Handles the callback from the external login page (secure.losspay.com)
 * 
 * Expected URL format: /portal/callback?token=<jwt_token>&returnUrl=<encoded_path>
 */
export default function PortalCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [error, setError] = useState(null);

  useEffect(() => {
    handleCallback();
  }, []);

  const handleCallback = async () => {
    const token = searchParams.get('token');
    const policiesParam = searchParams.get('policies');
    const returnUrl = searchParams.get('returnUrl') || '/portal';
    
    if (!token) {
      setStatus('error');
      setError('No authentication token received. Please try logging in again.');
      return;
    }

    try {
      // Validate the token with our backend
      const response = await fetch(`${API_URL}/api/auth/validate-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });

      const data = await response.json();

      if (!response.ok || !data.valid) {
        throw new Error(data.detail || 'Invalid or expired token');
      }

      // Use policies from URL param if available, otherwise from validation response
      let policies = data.policies || [];
      if (policiesParam) {
        try {
          policies = JSON.parse(decodeURIComponent(policiesParam));
        } catch (e) {
          console.warn('Could not parse policies param:', e);
        }
      }

      // Store the session using AuthContext
      login(token, policies, data.email, data.policy_number);
      
      setStatus('success');
      
      // Redirect to the intended destination after a brief success message
      setTimeout(() => {
        const destination = decodeURIComponent(returnUrl);
        // Use window.location for more reliable cross-subdomain navigation
        window.location.href = `${window.location.origin}${destination}`;
      }, 1500);

    } catch (err) {
      console.error('Callback error:', err);
      setStatus('error');
      setError(err.message || 'Authentication failed. Please try again.');
    }
  };

  const handleRetry = () => {
    // Redirect to internal login as fallback
    const returnUrl = searchParams.get('returnUrl') || '/portal';
    navigate(`/portal/login?returnUrl=${encodeURIComponent(returnUrl)}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        {status === 'processing' && (
          <>
            <Loader2 className="h-16 w-16 text-blue-600 animate-spin mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              Verifying Your Login
            </h1>
            <p className="text-slate-600">
              Please wait while we complete your authentication...
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              Welcome Back!
            </h1>
            <p className="text-slate-600">
              Redirecting you to your dashboard...
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="h-10 w-10 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              Authentication Failed
            </h1>
            <p className="text-slate-600 mb-6">
              {error}
            </p>
            <div className="space-y-3">
              <button
                onClick={handleRetry}
                className="w-full px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={() => navigate('/')}
                className="w-full px-6 py-3 bg-slate-100 text-slate-700 font-medium rounded-lg hover:bg-slate-200 transition-colors"
              >
                Go to Home
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
